import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { authUtils } from '@/utils/auth';
import { AUTH_USER_CHANGED_EVENT } from '@/utils/token-storage';
import { useLogout } from '@/hooks/use-auth';
import { useTheme } from '@/hooks/use-theme';
import { LogOut, User as UserIcon, Settings, Sun, Moon, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';

// Sprint 38 — Contractual Precision minimal top bar.
//
// Contents (per DESIGN.md §3.3):
//   - Hamburger trigger (mobile only) — opens MobileDrawer
//   - Brand wordmark
//   - (no notifications bell, no global search, no org switcher,
//      no per-page CTA — pages own their own CTAs in-content)
//   - Profile menu with name, role, theme toggle, profile link, logout

interface TopBarProps {
  onOpenMobileDrawer?: () => void;
}

export function TopBar({ onOpenMobileDrawer }: TopBarProps) {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { theme, setTheme } = useTheme();

  const [currentUser, setCurrentUser] = useState(() => authUtils.getCurrentUser());
  useEffect(() => {
    const sync = () => setCurrentUser(authUtils.getCurrentUser());
    window.addEventListener(AUTH_USER_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, sync);
  }, []);
  const userName = (currentUser?.fullName || 'User').replace(/^\[.*?\]\s*/, '');
  const userInitials = userName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => navigate('/login'),
      onError: () => navigate('/login'),
    });
  };

  return (
    <header
      role="banner"
      className="sticky top-0 z-40 flex h-14 min-w-0 shrink-0 items-center justify-between gap-3 border-b-2 border-border bg-background px-4 sm:px-6"
    >
      <div className="flex min-w-0 items-center gap-3">
        {onOpenMobileDrawer && (
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onOpenMobileDrawer}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        <span className="font-newsreader text-xl font-semibold tracking-tight text-foreground">
          LarkPM
        </span>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-10 items-center gap-2 px-2 normal-case tracking-normal"
            aria-label="Open user menu"
          >
            <Avatar className="h-7 w-7 rounded-none">
              <AvatarImage src="/avatar.jpg" alt="Profile" />
              <AvatarFallback className="bg-foreground text-background text-xs font-bold rounded-none">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-semibold text-foreground sm:inline">
              {userName}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56 rounded-none border-2 border-border"
          sideOffset={8}
        >
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span className="font-bold text-foreground">{userName}</span>
              {currentUser?.email && (
                <span className="text-xs font-normal text-foreground-soft">
                  {currentUser.email}
                </span>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/profile')}>
            <UserIcon className="mr-2 h-4 w-4" />
            Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-foreground-soft">
            Appearance
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setTheme('light')} className={cn(theme === 'light' && 'font-bold')}>
            <Sun className="mr-2 h-4 w-4" /> Light
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('dark')} className={cn(theme === 'dark' && 'font-bold')}>
            <Moon className="mr-2 h-4 w-4" /> Dark
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('auto')} className={cn(theme === 'auto' && 'font-bold')}>
            <Monitor className="mr-2 h-4 w-4" /> System
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
